/* Auswahlfelder für Assets am Objekt (Sprite, Sound, Skybox, WAD, Textur) mit Schnellsuche und Mini-Vorschau. */
import { useEffect, useMemo, useState } from 'react';
import { filterEntries } from '../../model/assets.ts';
import type { AssetEntry, AssetKind } from '../../model/assets.ts';
import { useT } from '../hooks.ts';
import { TexCanvas } from '../texture/widgets.tsx';
import { AssetSelect } from './AssetSelect.tsx';
import type { SelectItem } from './AssetSelect.tsx';
import type { AssetHost } from './host.ts';
import { PixelsView, usePixels } from './PixelsView.tsx';

const kb = (n: number): string => (n >= 1 << 20 ? (n / (1 << 20)).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB');

type Item = SelectItem & { e: AssetEntry };

function Mini({ host, e }: { host: AssetHost; e: AssetEntry }) {
  const px = usePixels(e.kind + ':' + e.key + (e.loaded ? ':l' : ''), () => (e.kind === 'sprite' || e.kind === 'skybox') ? host.preview(e) : Promise.resolve(null));
  if (e.kind === 'sound') return <span className="ab-ico sm" aria-hidden="true">♪</span>;
  if (e.kind === 'wad') return <span className="ab-ico sm" aria-hidden="true">▤</span>;
  if (e.kind === 'model') return <span className="ab-ico sm" aria-hidden="true">◈</span>;
  return <PixelsView px={px} />;
}

/** Auswahlfeld für eine Textur: alle Texturen der geladenen WADs (benutzte zuerst) mit Mini-Vorschau; Enter übernimmt auch freien Text.
 * Ohne value/onPick gilt die aktive Textur des Editors. */
export function TexSelect(props: { host: AssetHost; label: string; value?: string; onPick?: (name: string) => void; browse?: boolean }) {
  const t = useT();
  const { host } = props;
  const tx = host.textures();
  const usedSet = new Set(tx.used.map((u) => u.name.toUpperCase()));
  const items = useMemo(() => tx.loaded.map((n): SelectItem => ({ value: n, label: n, hint: usedSet.has(n.toUpperCase()) ? '●' : undefined })).sort((a, b) => Number(!!b.hint) - Number(!!a.hint)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tx.version, tx.loaded.length, tx.used.map((u) => u.name).join('|')]);
  const pick = props.onPick || ((n: string) => host.setActiveTex(n, false));
  return (
    <div className="ksel">
      <AssetSelect items={items} ariaLabel={props.label} value={props.value ?? tx.active} placeholder={props.label} emptyText={t('pn.no_wad')}
        renderPreview={(it) => <TexCanvas name={it.value} wads={{ pixels: host.texPixels }} version={tx.version} later />}
        onPick={(it) => pick(it.value)} onFree={pick} />
      {props.browse !== false && <button className="btn sm" onClick={() => host.open('texture')}>{t('ab.browse')}</button>}
    </div>
  );
}

/** Auswahlfeld für einen Wert am Objekt (Sprite, Sound, Skybox, WAD): alle Assets der Art, Schnellsuche, Mini-Vorschau.
 * Die Auswahl setzt format(Eintrag) und lädt ein noch nicht geladenes Server-Asset in den Editor; freier Text bleibt möglich. */
export function AssetField(props: { host: AssetHost; kind: AssetKind; value: string; ariaLabel: string; placeholder?: string; format?: (e: AssetEntry) => string; onCommit: (v: string) => void; /** freier Text (Standard: onCommit) */ onFree?: (v: string) => void }) {
  const t = useT();
  const { host, kind } = props;
  const [, bump] = useState(0);
  useEffect(() => host.subscribe(() => bump((n) => n + 1)), [host]);
  const entries = host.entries();
  const items = useMemo<Item[]>(() => filterEntries(entries, { query: '', onlyUsed: false, kind }).map((e) => ({
    value: e.kind + ':' + e.key, label: e.label, e,
    hint: e.loaded ? '✓' : e.onServer ? kb(e.size) : t('ab.missing_noserver'), dim: !e.onServer && !e.loaded
  })), [entries, kind, t]);
  const fmt = props.format || ((e: AssetEntry) => e.label);
  return (
    <AssetSelect items={items} ariaLabel={props.ariaLabel} value={props.value} placeholder={props.placeholder} emptyText={t('ab.none')}
      renderPreview={(it) => <Mini host={host} e={(it as Item).e} />}
      onPick={(it) => { const e = (it as Item).e; props.onCommit(fmt(e)); if (!e.loaded && e.onServer) void host.load(e); }}
      onFree={props.onFree ?? props.onCommit} />
  );
}
